'use client';

import { useState } from 'react';
import { Card, CardContent } from '@av/ui/card';
import { ImgNotAvailable } from '@/assets/img-not-available';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@av/ui/carousel';
import { formatSrcSet, ImageArray, ProductImage } from '@av/utils';
import { cn } from '@av/utils';

interface ImageCarouselProps {
  productTitle: string;
  mainProductImage?: string | null;
  productImages?: ProductImage[];
  srcSet?: string;
}

export default function ImageCarousel({
  productTitle,
  mainProductImage,
  productImages = [],
  srcSet,
}: ImageCarouselProps) {
  const [selectedImageObj, setSelectedImageObj] = useState<ImageArray | null>(
    productImages[0]?.conversions || null
  );
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [hasError, setHasError] = useState(false);

  const filteredImages = productImages.filter(
    (item) => item?.conversions && Array.isArray(item.conversions) && item.conversions.length > 0
  );

  if (!filteredImages.some((item) => item.is_main === true) && mainProductImage) {
    filteredImages.unshift({
      conversions: ['160', '320', '640', '800'].map((size) => ({
        type: size,
        url: mainProductImage,
      })),
      is_main: true,
    } as ProductImage);
  }

  const selectedImageSrcSet = selectedImageObj ? formatSrcSet(selectedImageObj) : srcSet;
  const selectedImageUrl = productImages.length
    ? selectedImageObj?.[0]?.url || mainProductImage || ''
    : mainProductImage || '';
  const hasCarouselImages = filteredImages.length > 0;

  const handleSelectImage = (images: ImageArray, index: number) => {
    setSelectedImageObj(images);
    setSelectedIndex(index);
    setHasError(false);
  };

  return (
    <div className="h-min w-full grow space-y-4 md:max-w-[34rem]">
      <Card className="w-full max-md:hidden">
        <CardContent className="relative flex aspect-square h-min w-full p-5 sm:p-12 md:p-16">
          {selectedImageUrl && !hasError ? (
            <img
              src={selectedImageUrl}
              alt={productTitle}
              srcSet={selectedImageSrcSet}
              onError={() => setHasError(true)}
              className="m-auto size-full max-w-2xl object-contain"
              sizes="(max-width: 768px) calc(100vw - 72px), (min-width: 769px) and (max-width: 1268px) calc(50vw - 120px), (min-width: 1269px) 800px"
            />
          ) : (
            <ImgNotAvailable className="m-auto w-1/2" />
          )}
        </CardContent>
      </Card>

      {hasCarouselImages && (
        <Carousel
          className={cn('w-[calc(100%+2rem)] max-sm:-ml-4 sm:w-full', {
            'max-md:pointer-events-none': filteredImages.length <= 1,
            'md:hidden': filteredImages.length <= 1,
          })}
          opts={{
            loop: true,
            slidesToScroll: 1,
            align: 'start',
          }}
        >
          <CarouselContent className="-ml-0 sm:-ml-4">
            {filteredImages.map((item: ProductImage, index: number) => {
              const itemImages = item?.conversions || null;
              const itemImageUrl = itemImages?.[0]?.url || '';
              const thumbnailSrcSet = itemImages ? formatSrcSet(itemImages) : '';
              const isSelected = selectedIndex === index;

              return (
                <CarouselItem key={index} className="-pl-0 basis-full sm:basis-1/3 sm:pl-4">
                  <div
                    className={cn(
                      'aspect-square cursor-pointer overflow-hidden bg-white sm:rounded-md sm:border-2',
                      isSelected
                        ? 'sm:border-primary'
                        : 'sm:border-transparent sm:hover:border-primary/50'
                    )}
                    onClick={() => itemImages && handleSelectImage(itemImages, index)}
                  >
                    {itemImageUrl ? (
                      <img
                        src={itemImageUrl}
                        srcSet={thumbnailSrcSet}
                        alt={`${productTitle} - Image ${index + 1}`}
                        className="size-full object-contain"
                        sizes="(max-width: 768px) calc(100vw - 32px), (min-width: 640px) and (max-width: 1024px) 33vw, 150px"
                        onError={(e) => {
                          if (mainProductImage) {
                            e.currentTarget.src = mainProductImage;
                            e.currentTarget.srcset = '';
                          }
                        }}
                      />
                    ) : (
                      <div className="flex size-full items-center justify-center bg-muted">
                        <ImgNotAvailable className="w-1/2" />
                      </div>
                    )}
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <CarouselPrevious
            className={cn('left-2 md:-left-4 size-8 md:size-9', {
              'md:hidden': filteredImages.length <= 3,
              'max-md:hidden': filteredImages.length <= 1,
            })}
          />
          <CarouselNext
            className={cn('right-2 md:-right-4 size-8 md:size-9', {
              'md:hidden': filteredImages.length <= 3,
              'max-md:hidden': filteredImages.length <= 1,
            })}
          />
        </Carousel>
      )}
    </div>
  );
}
